import { useEffect, useState } from "react";
import { Box, Typography, Button, Paper, Grid, Skeleton } from "@mui/material";
import AddTestimonialModal from "./components/AddTestimonials";
import { useTestimonials } from "./hooks/UseTestimonials";

const TestimonialsManager = () => {
  const { testimonials, loading, error, fetchTestimonials, saveTestimonial } =
    useTestimonials();

  const [open, setOpen] = useState(false);

  useEffect(() => {
    fetchTestimonials();
  }, [fetchTestimonials]);

  const handleAddTestimonial = async (data: {
    author_name: string;
    author_description?: string;
    content: string;
    rating?: number;
  }) => {
    await saveTestimonial({
      author_name: data.author_name,
      author_description: data.author_description,
      content: data.content,
      rating: data.rating ?? 5,
      is_active: true,
    });
  };

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          mb: 4,
        }}
      >
        <Button
          variant="contained"
          onClick={() => setOpen(true)}
          disabled={loading}
        >
          Add Testimonial
        </Button>
      </Box>

      {/* Error State */}
      {error && (
        <Typography color="error" align="center" sx={{ mb: 3 }}>
          {error}
        </Typography>
      )}

      {/* Loading State */}
      {loading && (
        <Grid container spacing={4}>
          {Array.from({ length: 4 }).map((_, index) => (
            <Grid key={index} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <Paper sx={{ p: 4, borderRadius: 4 }}>
                <Skeleton variant="text" height={80} />
                <Skeleton
                  variant="text"
                  height={30}
                  sx={{ width: "60%", mx: "auto" }}
                />
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Testimonials Grid */}
      {!loading && testimonials.length > 0 && (
        <Grid container spacing={4}>
          {testimonials.map((testimonial) => (
            <Grid key={testimonial.id} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <Paper
                sx={{
                  p: 4,
                  borderRadius: 4,
                  textAlign: "center",
                  height: "100%",
                  boxShadow: 0,
                }}
              >
                <Typography sx={{ lineHeight: 1.7, mb: 3 }}>
                  {testimonial.content}
                </Typography>

                <Typography
                  sx={{
                    fontFamily: "Brittany, cursive",
                    fontSize: 24,
                  }}
                >
                  {testimonial.author_name}
                </Typography>

                {testimonial.author_description && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    {testimonial.author_description}
                  </Typography>
                )}
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Empty State */}
      {!loading && testimonials.length === 0 && !error && (
        <Typography align="center" color="text.secondary">
          No testimonials yet.
        </Typography>
      )}

      {/* Modal */}
      <AddTestimonialModal
        open={open}
        onClose={() => setOpen(false)}
        onSave={handleAddTestimonial}
      />
    </Box>
  );
};

export default TestimonialsManager;
